import { useContext, useEffect, useState } from "react"
import { useParams } from "react-router-dom"

import { getEntityBySlug } from "../services/chaperonConnector"
import { getPlaceDetails, searchPlaces } from "../services/placesConnector"
import {
    signUpClickHandler,
    comingSoonClickHandler,
    consoleLog,
    colorCode,
    safetyPercentage,
} from "../utilities/helpers"
import VicinityContext from "../context/vicinityContext"
import BreadCrumbs from "../components/BreadCrumbs"
import PageHeading from "../components/PageHeading"
import ComingSoon from "../components/ComingSoon"
import PlaceProfileHolder from "../components/PlaceProfileHolder"
import ReportCarousel from "../components/ReportCarousel"
import PlaceSummary from "../components/PlaceSummary"
import RiskProfileColors from "../components/RiskProfileColors"
import PlaceBadge from "../components/PlaceBadge"
import SidebarSafetyProfile from "../components/shared/SidebarSafetyProfile"
import { SESSIONSTORAGE_ENTITY, LOCALSTORAGE_ENTITYMAP } from "../const/storage"
import { CHAPERON_SHARE_URL, FLAGS_API_URL } from "../const/paths"
import PlaceSafetyProfile from "../components/PlaceSafetyProfile"
import PlaceConcerns from "../components/PlaceConcerns"
import SharePreviewCard from "../components/SharePreviewCard"
import { useAuth } from "../hooks/useAuth"

export default function SafetyProfile() {
    const { slug } = useParams()
    const isLoggedIn = useAuth()
    let place = useContext(VicinityContext).place
    const [cardPlace, setCardPlace] = useState({})
    const [isSidebarOpen, setIsSidebarOpen] = useState(true)
    useEffect(() => {
        async function getCardPlace() {
            // Use slug from useParams instead of parsing from window.location
            // Logged out, the page shows SharePreviewCard instead, which reads
            // the public summary; these calls need a login.
            if (!slug || !isLoggedIn) return

            const entityStored = JSON.parse(
                sessionStorage.getItem(SESSIONSTORAGE_ENTITY)
            )

            // Check if the entity is already in session storage
            // and if it matches the current slug (based on the path)
            if (entityStored?.slug === slug) {
                setCardPlace(entityStored)
                consoleLog("Loaded entity from session storage.")
            } else {
                // If not, check if a place is already set in context
                if (place) {
                    setCardPlace(place)
                }

                let entityMapping = JSON.parse(
                    localStorage.getItem(`${LOCALSTORAGE_ENTITYMAP}`)
                )
                consoleLog("Entity mapping Array:", entityMapping)
                const mapped = entityMapping?.find(
                    (e) => e.slug === slug
                )?.reference
                consoleLog("Mapped entity:", mapped)

                const entity = await getEntityBySlug(slug)

                // Google Maps API does not allow storing Place IDs permanently
                // So we need to search for the place again using the entity name and country
                // locationBias is included to prevent the search from being IP biased (not yet functional)
                // (https://developers.google.com/maps/documentation/places/web-service/legacy/overview-legacy)
                // TODO: Upgrade from Leagacy to New Places API

                const places = await searchPlaces(
                    `${entity[0].name}, ${entity[0].country_code}`
                )

                const placeDetails = await getPlaceDetails(
                    mapped || places[0]?.place_id
                )

                if (places?.length > 0 && entity?.length > 0) {
                    let placeData = entity[0]
                    placeData.fromGoogle = placeDetails
                    setCardPlace(placeData)
                    sessionStorage.setItem(
                        SESSIONSTORAGE_ENTITY,
                        JSON.stringify(placeData)
                    )
                }
            }
        }

        getCardPlace()
    }, [place, slug, isLoggedIn])

    // Update document metadata when cardPlace changes
    useEffect(() => {
        if (cardPlace?.name) {
            const title = `Chaperon - Safety profile for ${cardPlace.name}, ${cardPlace.country}`
            const description = `View the safety profile and risk assessment for ${cardPlace.name}, ${cardPlace.country}. Get real-time safety scores, local insights, and travel recommendations.`
            const url = `https://chaperonapp.com/analysis/${cardPlace.slug}`
            // The chaperon.app safety-level card, never the Google photo:
            // that is Google content, and every crawler fetch of it is a
            // billed Places photo call.
            const image = `${CHAPERON_SHARE_URL}/i/${encodeURIComponent(cardPlace.slug)}.png`

            // Update document title
            document.title = title

            // Helper to update or create meta tags
            const updateMetaTag = (selector, attribute, value) => {
                let element = document.querySelector(selector)
                if (element) {
                    element.setAttribute(attribute, value)
                } else {
                    element = document.createElement("meta")
                    const [attr, val] = selector
                        .replace("meta[", "")
                        .replace("]", "")
                        .split("=")
                    element.setAttribute(attr, val.replace(/"/g, ""))
                    element.setAttribute(attribute, value)
                    document.head.appendChild(element)
                }
            }

            // Primary meta tags
            updateMetaTag('meta[name="title"]', "content", title)
            updateMetaTag('meta[name="description"]', "content", description)

            // Open Graph
            updateMetaTag('meta[property="og:type"]', "content", "website")
            updateMetaTag(
                'meta[property="og:site_name"]',
                "content",
                "Chaperon"
            )
            updateMetaTag('meta[property="og:url"]', "content", url)
            updateMetaTag('meta[property="og:title"]', "content", title)
            updateMetaTag(
                'meta[property="og:description"]',
                "content",
                description
            )
            updateMetaTag('meta[property="og:image"]', "content", image)
            updateMetaTag('meta[property="og:image:width"]', "content", "1200")
            updateMetaTag('meta[property="og:image:height"]', "content", "630")
            updateMetaTag(
                'meta[property="og:image:alt"]',
                "content",
                `Safety profile for ${cardPlace.name}`
            )

            // Twitter
            updateMetaTag(
                'meta[name="twitter:card"]',
                "content",
                "summary_large_image"
            )
            updateMetaTag('meta[name="twitter:url"]', "content", url)
            updateMetaTag('meta[name="twitter:title"]', "content", title)
            updateMetaTag(
                'meta[name="twitter:description"]',
                "content",
                description
            )
            updateMetaTag('meta[name="twitter:image"]', "content", image)

            // Canonical URL
            let canonical = document.querySelector('link[rel="canonical"]')
            if (canonical) {
                canonical.setAttribute("href", url)
            } else {
                canonical = document.createElement("link")
                canonical.setAttribute("rel", "canonical")
                canonical.setAttribute("href", url)
                document.head.appendChild(canonical)
            }

            consoleLog("Meta tags updated for:", cardPlace.name)
        }
    }, [cardPlace])

    // "Analysis" (matching the Android app's own name for this screen) links
    // to the user's current location's place -- `place` is what the rest of
    // the site (e.g. the home page's hero card) already treats as that,
    // whenever real device coordinates back it up (Vicinitizer resolves it
    // from `location` on every page, this one included). Without a
    // location, the only other way `place` gets set is the search bar's own
    // place select, so the same value still stands in for "last place
    // searched" there. With no place at all yet, "Analysis" just goes home.
    const analysisPlace = place?.slug ? place : null

    const crumbs = [
        {
            path: analysisPlace ? `/analysis/${analysisPlace.slug}` : "/",
            name: "Analysis",
        },
        {
            path: `/analysis/${cardPlace?.slug}`,
            name: `${cardPlace?.name}`,
        },
    ]

    let placePhoto = null
    placePhoto = Array.isArray(cardPlace?.fromGoogle?.photos)
        ? cardPlace.fromGoogle.photos[0] ?? null
        : null

    if (!isLoggedIn) {
        return (
            <div className="App-content">
                <div className="container pt-5 pb-4 mt-4">
                    <SharePreviewCard slug={slug} />
                </div>
            </div>
        )
    }

    return (
        <div>
            <BreadCrumbs crumbs={crumbs} />
            <ComingSoon />
            <div className="dashboard-container">
                <PageHeading title="Analysis" />
            </div>
            <div className="App-content">
                <div className="container-fluid">
                    {/* Hero Section with Place Image */}
                    {cardPlace?.name ? (
                        <div className="row justify-content-center mb-4">
                            <div className="col-12">
                                <div
                                    id="hero-image"
                                    className="hero-image-container position-relative"
                                    style={{
                                        height: "400px",
                                        backgroundImage: cardPlace?.fromGoogle
                                            ?.photo_cover?.photo_ref
                                            ? `url(${cardPlace.fromGoogle.photo_cover.photo_ref})`
                                            : `url(${FLAGS_API_URL}/${cardPlace?.country_code}.svg)`,
                                        backgroundSize: "cover",
                                        backgroundPosition: "center center",
                                        backgroundRepeat: "no-repeat",
                                        backgroundAttachment: "scroll",
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                    }}
                                >
                                    {/* Overlay for better text readability */}
                                    <div
                                        className="position-absolute w-100 h-100"
                                        style={{
                                            background:
                                                "linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.7))",
                                            borderRadius: "8px",
                                        }}
                                    ></div>

                                    {/* Place Title Overlay */}
                                    <div className="position-absolute bottom-0 start-0 p-4 text-white">
                                        <h1
                                            className="display-4 fw-bold ms-4"
                                            style={{
                                                textShadow:
                                                    "2px 2px 4px rgba(0,0,0,0.8)",
                                                color: "#ffffff",
                                            }}
                                        >
                                            {cardPlace.name}
                                        </h1>
                                        <p
                                            className="lead mb-0"
                                            style={{
                                                textShadow:
                                                    "1px 1px 3px rgba(0,0,0,0.8)",
                                                color: "#ffffff",
                                            }}
                                        >
                                            {cardPlace.country}
                                        </p>
                                    </div>

                                    {/* Place Photo Credit */}
                                    {placePhoto && (
                                        <div
                                            className="position-absolute bottom-0 end-0 p-4"
                                            style={{
                                                textAlign: "right",
                                            }}
                                        >
                                            <span
                                                id="place-photo-credit"
                                                className="small muted text-plain"
                                                style={{
                                                    textDecoration: "none",
                                                    color: "#dbdbdbff",
                                                    opacity: 1.4,
                                                }}
                                            >
                                                photo by:&nbsp;
                                                <span
                                                    dangerouslySetInnerHTML={{
                                                        __html: placePhoto
                                                            ?.html_attributions[0],
                                                    }}
                                                />
                                            </span>
                                        </div>
                                    )}

                                    {/* Risk Badge Overlay */}
                                    <div
                                        id="risk-badge-overlay"
                                        className="position-absolute top-0 start-0 p-1"
                                        style={{
                                            transform: "scale(2.5)",
                                            transformOrigin: "top left",
                                        }}
                                    >
                                        <PlaceBadge
                                            place={cardPlace}
                                            mode="mini hero-overlay"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="row justify-content-center mb-4">
                            <div className="col-12">
                                <PlaceProfileHolder />
                            </div>
                        </div>
                    )}

                    {/* Progress Bar Section */}
                    {cardPlace?.name && (
                        <div className="row justify-content-center mb-4">
                            <div className="col-lg-8">
                                <div
                                    className="progress mb-3"
                                    style={{
                                        height: "30px",
                                        borderRadius: "15px",
                                    }}
                                >
                                    <div
                                        className={`progress-bar bg-${colorCode(
                                            safetyPercentage(
                                                cardPlace?.overall_safety
                                            )
                                        )}`}
                                        role="progressbar"
                                        aria-valuenow={
                                            100 -
                                            safetyPercentage(
                                                cardPlace?.overall_safety
                                            )
                                        }
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                        style={{
                                            width: `${safetyPercentage(
                                                cardPlace?.overall_safety
                                            )}%`,
                                            borderRadius: "15px",
                                        }}
                                    >
                                        <strong className="ms-3">
                                            SAFETY:{" "}
                                            {safetyPercentage(
                                                cardPlace?.overall_safety
                                            )}
                                            %
                                        </strong>
                                    </div>
                                </div>
                                <div className="text-center">
                                    <RiskProfileColors />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Place Profile Section */}
                    <div className="row justify-content-center mb-4">
                        <div className="col-lg-10">
                            <div id="place-profile">
                                {cardPlace?.name ? (
                                    <PlaceSafetyProfile cardPlace={cardPlace} />
                                ) : (
                                    <PlaceProfileHolder />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons Section */}
                    <div className="row justify-content-center mb-5">
                        <div className="col-lg-8">
                            <div className="d-flex justify-content-center flex-wrap gap-3">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-lg"
                                    onClick={comingSoonClickHandler}
                                >
                                    Get insurance
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-lg"
                                    onClick={comingSoonClickHandler}
                                >
                                    Book travel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-lg"
                                    onClick={comingSoonClickHandler}
                                    disabled
                                >
                                    Download apps
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Content Sections */}
                    <div className="row justify-content-center">
                        <div className="col-lg-10 col-xl-8">
                            {/* Place Summary Section */}
                            <div className="mb-5">
                                <PlaceSummary
                                    placeId={cardPlace?.fromGoogle?.place_id}
                                />
                            </div>

                            {/* Reports Section */}
                            <div className="mb-5">
                                <ReportCarousel />
                            </div>

                            {/* Sign Up Section */}
                            <PlaceConcerns cardPlace={cardPlace} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating Safety Analysis Sidebar */}
            <SidebarSafetyProfile
                cardPlace={cardPlace}
                isSidebarOpen={isSidebarOpen}
                setIsSidebarOpen={setIsSidebarOpen}
            />
        </div>
    )
}
